import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { TabPanel, TabView } from "primereact/tabview";

import Loader from "../../components/Loader";
import useProspectDetail from "../../../hooks/useProspectDetail";
import useProspectStations from "../../../hooks/useProspectStations";
import useProspectJourney from "../../../hooks/useProspectJourney";
import useContractTimeline from "../../../hooks/useContractTimeline";
import useProspectSetupFee from "../../../hooks/useProspectSetupFee";
import useProspectReminders from "../../../hooks/useProspectReminders";
import useStationProvisioning from "../../../hooks/useStationProvisioning";
import { isOnboardingEnabled } from "../../../config/featureFlags";
import EroundJourney from "./components/EroundJourney";
import ProspectTypeTag from "./components/ProspectTypeTag";
import { isCustomerPromoted } from "../../../utils/setupFee";
import { stateLabel, stateSeverity } from "../../../utils/onboardingStates";
import {
  initialProspectTab,
  ProspectTabKey,
  prospectTabKeys,
  prospectTabLabel,
} from "../../../utils/prospectTabs";
import { isJourneyEnded } from "../../../utils/prospectJourney";
import { contractChase } from "../../../utils/contractChase";
import { contractTimestamps } from "../../../utils/contractTimestamps";
import {
  NextStepAction,
  nextStepFor,
} from "../../../utils/prospectNextStep";
import { stationSetup } from "../../../utils/stationSetup";
import NextStepBar from "./components/NextStepBar";
import ProspectFields from "./components/ProspectFields";
import JourneyProgress from "./components/JourneyProgress";
import AuditHistoryTable from "./components/AuditHistoryTable";
import StatusActionsBar from "./components/StatusActionsBar";
import ContractPanel from "./components/ContractPanel";
import ContractActivity from "./components/ContractActivity";
import StationConfigPanel from "./components/StationConfigPanel";
import EroundSetupDialog from "./components/EroundSetupDialog";
import EichrechtDecisionDialog, { specsText } from "./components/EichrechtDecisionDialog";
import SetupFeeCard from "./components/SetupFeeCard";
import RemindersCard from "./components/RemindersCard";

/**
 * Prospect detail (US1): all fields + state-transition timeline + edit-audit
 * history. Hosts the status / edit / technical-name actions (US2/US3) via
 * StatusActionsBar.
 *
 * The sections are split across tabs. Stacked, the screen had grown past the
 * point where the panel an agent came to act in was visible at all — so the
 * fields, the contract, the station configuration and the journey each get their
 * own tab, and the screen opens on the one that holds the next step
 * (see utils/prospectTabs). What identifies the prospect and what can be done to
 * it — the name, the stage, the status actions — stays above the tabs, since
 * those apply whichever tab is open.
 */
const ProspectDetailPage: React.FC = () => {
  const { id = "" } = useParams();
  const toast = useRef<Toast>(null);
  const detail = useProspectDetail({ id, toastRef: toast });
  const { prospect, transitions, audit, loading, error } = detail;
  // Station access data is a separate fetch from the prospect, so it gets its
  // own hook — but the progression actions change the prospect's stage, hence
  // handing them the prospect's reload as well.
  const stations = useProspectStations({
    id,
    toastRef: toast,
    reloadProspect: detail.reload,
    stage: prospect?.current_state,
  });
  // Owned here rather than inside the contract activity section: the header's
  // chase prompt is decided by the same reminders that section lists, and two
  // fetches of the same timeline could disagree.
  const timeline = useContractTimeline({ id, toastRef: toast });
  // The setup fee (CWA-196) is its own fetch, and only once it can be due.
  const setupFee = useProspectSetupFee({
    id,
    toastRef: toast,
    enabled: !!prospect && isCustomerPromoted(prospect.current_state),
  });
  // CWA-189: the reminder ladder comes with the detail, so its actions just reload it.
  const reminders = useProspectReminders({ id, toastRef: toast, reload: detail.reload });
  // Derived only — reads what the detail hook already loaded, no extra fetch.
  const journey = useProspectJourney({
    currentState: prospect?.current_state,
    transitions,
    moreInfoNeeded: prospect?.more_info_needed,
    audit,
    setupFee: setupFee.setupFee,
  });

  // The action the next-step button asked for, held until the panel that owns it
  // has started it. The button opens the panel's own confirmation rather than
  // acting from the header, so a step is always taken in front of its context.
  const [requested, setRequested] = useState<NextStepAction | null>(null);
  // The station a requested eRound review is for.
  const [requestedStation, setRequestedStation] = useState<string | null>(null);
  // "Set up in eRound + OCPP" (CWA-231) is its own pop-up, opened from the header.
  const [setupOpen, setSetupOpen] = useState(false);
  // CWA-230: support's Eichrecht check, also its own pop-up.
  const [eichrechtOpen, setEichrechtOpen] = useState(false);
  // Set while a step the header started is still running, so the wait shows on
  // the button that was pressed rather than on the menu beside it.
  const [stepBusy, setStepBusy] = useState(false);
  // Null until an agent picks a tab themselves; the stage decides until then.
  const [picked, setPicked] = useState<ProspectTabKey | null>(null);
  // Opening a different prospect starts from its own stage rather than keeping
  // the tab chosen for the previous one.
  useEffect(() => setPicked(null), [id]);

  const state = prospect?.current_state;
  const tabs = state ? prospectTabKeys(state) : [];
  // An unsigned contract that has been out for a week becomes the next step
  // itself — nothing chases the customer on its own.
  const chase = prospect
    ? contractChase({
        state: prospect.current_state,
        sentAt: contractTimestamps(prospect).sentAt,
        events: timeline.events,
      })
    : null;
  // Each station's eRound setup (SOPS-148), read once for the next-step bar,
  // the progress tab and the stations table, so none of them can disagree.
  const stationIds = isOnboardingEnabled ? prospect?.technical_names ?? [] : [];
  const provisioning = useStationProvisioning(stationIds, state);
  const setup =
    state && isOnboardingEnabled
      ? stationSetup({
          currentState: state,
          transitions: transitions ?? [],
          stationIds,
          byStation: provisioning.byStation,
        })
      : null;
  const nextStep = state
    ? nextStepFor(
        state,
        prospect?.sign_method,
        chase,
        setupFee.setupFee?.status,
        setup?.next,
        prospect?.config_email_dispatch,
        setup?.afterPromotion,
        prospect?.eichrecht_review,
        prospect?.reminders
      )
    : null;

  // A step whose controls live in a panel opens that panel's tab first; the two
  // header-owned ones (station IDs, contract draft) stay where they are.
  const runNextStep = (action: NextStepAction) => {
    if (nextStep?.tab) setPicked(nextStep.tab);
    if (action === "setup-eround") {
      setSetupOpen(true);
      return;
    }
    if (action === "check-eichrecht") {
      setEichrechtOpen(true);
      return;
    }
    setRequestedStation(nextStep?.stationId ?? null);
    setRequested(action);
  };
  // An action can retire the tab it was taken in — confirming the access data
  // moves the prospect on — so a choice that no longer exists falls back to the
  // stage rather than leaving nothing selected.
  const activeTab =
    picked && tabs.includes(picked)
      ? picked
      : state
      ? initialProspectTab(state)
      : "details";

  const tabContent = (key: ProspectTabKey): React.ReactNode => {
    if (!prospect) return null;
    switch (key) {
      case "contract":
        return (
          <>
            <ContractPanel
              prospect={prospect}
              detail={detail}
              requestedAction={requested}
              onRequestHandled={() => setRequested(null)}
            />
            <ContractActivity
              timeline={timeline}
              chase={chase}
              requestedAction={requested}
              onRequestHandled={() => setRequested(null)}
            />
          </>
        );
      case "stations":
        return (
          <>
            <StationConfigPanel
              prospect={prospect}
              stations={stations}
              lastGoLiveAttempt={journey.lastGoLiveAttempt}
              provisioning={isOnboardingEnabled ? provisioning : undefined}
              requestedAction={requested}
              requestedStationId={requestedStation}
              onRequestHandled={() => setRequested(null)}
            />
            <SetupFeeCard
              setupFee={setupFee}
              requestedAction={requested}
              onRequestHandled={() => setRequested(null)}
            />
          </>
        );
      case "progress":
        return (
          // One timeline, not two: the former "Status history" listed the same
          // states with the same timestamps in reverse, so its only unique
          // content — who did each step, and the resignation reason — moved into
          // the progression instead.
          <div className="mb-2">
            {/* CWA-189: what has been done about the wait, next to the journey it is part of. */}
            <RemindersCard
              prospectId={prospect.id}
              reminders={prospect.reminders}
              moreInfoNeeded={prospect.more_info_needed}
              actions={reminders}
              requestedAction={requested}
              onRequestHandled={() => setRequested(null)}
            />
            <h3 className="mt-0">Onboarding progress</h3>
            {setup ? (
              <EroundJourney
                stationIds={stationIds}
                steps={journey.steps}
                setup={setup}
                provisioning={provisioning}
                // The next-step bar's own words, so the two say the same.
                nextAction={nextStep?.description ?? null}
                ended={isJourneyEnded(prospect.current_state)}
              />
            ) : (
              <JourneyProgress
                steps={journey.steps}
                nextAction={journey.nextAction}
                ended={isJourneyEnded(prospect.current_state)}
              />
            )}
          </div>
        );
      // Where the journey says how far the prospect has come, this says what was
      // changed by hand on the way — a different question, asked at a different
      // time, so it gets its own tab rather than a heading below the timeline.
      case "history":
        return (
          <div className="mb-2">
            <h3 className="mt-0">Edit history</h3>
            <AuditHistoryTable audit={audit} />
          </div>
        );
      default:
        return <ProspectFields prospect={prospect} />;
    }
  };

  return (
    <div className="card onboarding-page">
      <Toast ref={toast} />

      {loading && <Loader />}

      {!loading && error && (
        <Message
          severity="error"
          text="This prospect could not be loaded."
          className="w-full"
        />
      )}

      {!loading && !error && prospect && (
        <>
          <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div className="flex align-items-center gap-2">
              <h2 className="m-0">
                {prospect.first_name} {prospect.last_name}
              </h2>
              <Tag
                className="prospect-status-pill"
                severity={stateSeverity(prospect.current_state)}
                value={stateLabel(prospect.current_state)}
              />
              {prospect.more_info_needed && (
                <Tag severity="info" value="More info needed" />
              )}
              <ProspectTypeTag prospect={prospect} linkToExistingCustomer />
            </div>
            <StatusActionsBar
              prospect={prospect}
              detail={detail}
              requestedAction={requested}
              onRequestHandled={() => setRequested(null)}
              onBusyChange={setStepBusy}
              setupFee={setupFee}
            />
          </div>

          {/* What to do next, given its own line above the tabs: it is the one
              thing an agent opens this screen for, and it belongs to the
              prospect rather than to any one tab. */}
          {nextStep && (
            <NextStepBar step={nextStep} busy={stepBusy} onRun={runNextStep} />
          )}
          {prospect && eichrechtOpen && (
            <EichrechtDecisionDialog
              manufacturer={prospect.eichrecht_review?.manufacturer ?? prospect.wallbox_manufacturer}
              model={prospect.eichrecht_review?.model ?? prospect.wallbox_model}
              details={specsText(prospect)}
              visible
              onHide={() => setEichrechtOpen(false)}
              onDecided={detail.reload}
            />
          )}
          {prospect && setupOpen && (
            <EroundSetupDialog
              prospect={prospect}
              stationIds={stationIds}
              byStation={provisioning.byStation}
              visible
              onHide={() => setSetupOpen(false)}
              onChanged={() => Promise.all([detail.reload(), provisioning.reload()])}
            />
          )}

          <TabView
            className="prospect-tabs"
            activeIndex={Math.max(tabs.indexOf(activeTab), 0)}
            onTabChange={(e) => setPicked(tabs[e.index])}
          >
            {tabs.map((key) => (
              <TabPanel key={key} header={prospectTabLabel(key)}>
                {tabContent(key)}
              </TabPanel>
            ))}
          </TabView>
        </>
      )}
    </div>
  );
};

export default ProspectDetailPage;
